/**
 * Format a date string to human-readable format
 * @param {string|Date} date - Date to format
 * @param {boolean} includeYear - Whether to include the year (default: true)
 * @returns {string} Formatted date like "March 12, 2025"
 */
export function formatHumanDate(date, includeYear = true) {
  let d
  
  // Check if date is a string in YYYY-MM-DD format (date-only, no time)
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    // Parse as local date to avoid timezone conversion
    const [year, month, day] = date.split('-').map(Number)
    d = new Date(year, month - 1, day)
  } else {
    // For all other formats (ISO with time, Date objects, etc.), use standard parsing
    d = new Date(date)
  }
  
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ]
  
  const day = d.getDate()
  const month = months[d.getMonth()]
  const year = d.getFullYear()
  
  if (includeYear) {
    return `${month} ${day}, ${year}`
  }
  return `${month} ${day}`
}

// Eastern Time helpers — DST-aware.
//
// Earlier these helpers hardcoded UTC-5 (EST), which silently corrupted any
// deadline saved between mid-March and early November (EDT, UTC-4). The form
// label says "Deadline Time (ET)", so an admin entering 11:08 AM during DST
// expected 11:08 EDT = 15:08 UTC, but easternToUtc shifted by 5h and stored
// 16:08 UTC (= 12:08 PM EDT). utcToEastern had the inverse bug, which made
// the round-trip *look* correct in the form (you got back 11:08), but the
// actual deadline instant was 1 hour late. auto_lock_brackets — which uses
// the real UTC value — therefore wouldn't fire when the admin's local clock
// said the deadline had passed. (Symmetric bugs hide each other.)
//
// Both functions now derive the offset for the actual calendar date via
// Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York' }), so EST
// (-5) and EDT (-4) are picked correctly.

const EASTERN_TZ = 'America/New_York'

// Returns the ms offset to ADD to a wall-clock-as-UTC value to get true UTC.
// At an EDT instant the offset is +4h * 3600s * 1000; at EST it's +5h.
function easternOffsetMsAt(utcMs) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).formatToParts(new Date(utcMs))
  const get = (t) => Number(parts.find((p) => p.type === t).value)
  // 'hour' may come back as '24' for midnight in some browsers — normalize.
  const hour = get('hour') % 24
  const easternMs = Date.UTC(
    get('year'), get('month') - 1, get('day'),
    hour, get('minute'), get('second')
  )
  return utcMs - easternMs
}

// Convert Eastern Time date/time to UTC ISO string for database storage.
// dateStr: 'YYYY-MM-DD', timeStr: 'HH:MM' (both in Eastern wall-clock time)
// Returns: ISO string in UTC (e.g., '2026-04-29T15:08:00.000Z' for 11:08 AM EDT)
// PostgreSQL TIMESTAMPTZ columns handle this automatically.
export function easternToUtc(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null

  const [year, month, day] = dateStr.split('-').map(Number)
  const [hour, minute] = timeStr.split(':').map(Number)
  if ([year, month, day, hour, minute].some(Number.isNaN)) return null

  // First-pass guess: treat the Eastern wall-clock as if it were UTC.
  const naiveUtcMs = Date.UTC(year, month - 1, day, hour, minute, 0)
  // Determine the Eastern offset that applies on this calendar date.
  const offsetMs = easternOffsetMsAt(naiveUtcMs)
  return new Date(naiveUtcMs + offsetMs).toISOString()
}

// Convert UTC ISO string to Eastern Time date and time strings for display/editing.
// utcString: ISO string (e.g., '2026-04-29T15:08:00.000Z')
// Returns: { date: 'YYYY-MM-DD', time: 'HH:MM' } as wall-clock in Eastern Time.
export function utcToEastern(utcString) {
  if (!utcString) return { date: null, time: '23:59' }

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(utcString))
  const get = (t) => parts.find((p) => p.type === t).value
  // 'hour' may come back as '24' for midnight in some browsers.
  const hour = (Number(get('hour')) % 24).toString().padStart(2, '0')

  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    time: `${hour}:${get('minute')}`,
  }
}

// Legacy function - kept for backward compatibility
// dateStr: 'YYYY-MM-DD', timeStr: 'HH:MM'
export function buildEasternIsoString(dateStr, timeStr) {
  return easternToUtc(dateStr, timeStr)
}

/**
 * Render a stored UTC deadline as the Eastern wall-clock phrase shown to
 * players: "11:59 pm ET on August 6, 2026".
 *
 * Goes through utcToEastern rather than formatting the UTC instant directly —
 * a deadline stored as 03:59Z on Aug 7 is 11:59 pm ET on Aug 6, so the naive
 * path reports the wrong day. Callers supply their own lead-in
 * ("Picks close at ...").
 *
 * @param {string} utcString - ISO UTC timestamp
 * @returns {string|null} phrase, or null when there's no usable deadline
 */
export function formatEasternDeadline(utcString) {
  if (!utcString) return null

  const { date, time } = utcToEastern(utcString)
  if (!date || !time) return null

  const [hourStr, minuteStr] = time.split(':')
  const hour24 = Number(hourStr)
  const minute = Number(minuteStr)
  if (Number.isNaN(hour24) || Number.isNaN(minute)) return null

  const period = hour24 >= 12 ? 'pm' : 'am'
  const hour12 = ((hour24 + 11) % 12) + 1

  return `${hour12}:${minute.toString().padStart(2, '0')} ${period} ET on ${formatHumanDate(date)}`
}

/**
 * Format a date and time to human-readable format
 * @param {string|Date} date - Date to format
 * @param {string} time - Time in HH:MM format (24-hour)
 * @returns {string} Formatted date and time like "October 31st, 2025 at 11:59 PM"
 */
// Note: formatDateTimeHuman was previously exported, but is currently unused.
// If needed in the future, it can be reintroduced as an exported helper.
